import React from 'react'

import AssetHandler from '@adUtilities/asset-handler/asset-handler'
import { setBackground } from '@adUtilities/asset-handler/asset-util'

const FALLBACK_IMAGE_URL = process.env.REACT_APP_PLACEHOLDER_IMAGE_URL || ''

export interface ComponentInterface {
  url: string
  className: string
  type?: 'legacy' | 'new'
  noSpliceUrl?: boolean
  bgProps?: {
    gradiant: number
  }
  alt?: string
  isImage?: boolean
  showFallbackImage?: boolean
  style?: React.CSSProperties
  children?: React.ReactNode
  preferCache?: boolean
}

const NONE_IMAGE_URL = 'none'

function ImageHandler({
  url,
  type,
  noSpliceUrl,
  className,
  bgProps,
  children,
  style,
  isImage,
  showFallbackImage = true,
  alt = '',
  preferCache = false,
}: ComponentInterface) {
  const isMounted = React.useRef(true)
  const imageRef = React.useRef<HTMLImageElement>(null)

  const assetUrl = React.useMemo(
    () =>
      url
        ? AssetHandler({
            url,
            type,
            noSpliceUrl,
          })
        : '',
    [url, type, noSpliceUrl]
  )

  const [isLoaded, setIsLoaded] = React.useState(() => {
    if (!assetUrl) {
      return false
    }
    const img = new Image()
    img.src = assetUrl
    return img.complete
  })

  const [bgImg, setBgImg] = React.useState(() => {
    if (!assetUrl) {
      return NONE_IMAGE_URL
    }

    const img = new Image()
    img.src = assetUrl
    if (img.complete) {
      const { backgroundImage } = setBackground(assetUrl, bgProps)
      return backgroundImage
    }

    return NONE_IMAGE_URL
  })

  React.useEffect(() => {
    if (!assetUrl) {
      return
    }

    const img = new Image()
    img.src = assetUrl

    if (img.complete) {
      if (!isMounted.current) {
        return
      }

      const { backgroundImage } = setBackground(assetUrl, bgProps)
      setBgImg(backgroundImage)
      setIsLoaded(true)
      return
    }

    img.onload = () => {
      if (!isMounted.current) {
        return
      }

      const { backgroundImage } = setBackground(assetUrl, bgProps)
      setBgImg(backgroundImage)
      setIsLoaded(true)
    }

    img.onerror = () => {
      if (!isMounted.current) {
        setIsLoaded(true)
        return
      }

      if (showFallbackImage) {
        if (imageRef.current) {
          imageRef.current.src = FALLBACK_IMAGE_URL
        }
        const { backgroundImage } = setBackground(assetUrl, { ...bgProps })
        setBgImg(`${backgroundImage}, url(${FALLBACK_IMAGE_URL})`)
      }

      setIsLoaded(true)
    }
  }, [assetUrl, bgProps, showFallbackImage])

  React.useEffect(
    () => () => {
      isMounted.current = false
    },
    []
  )

  if (isImage || (preferCache && !isLoaded)) {
    return <img ref={imageRef} className={className} alt={alt} src={assetUrl} />
  }

  return (
    <div
      className={`${className} transition-opacity duration-500 ${
        isLoaded ? 'opacity-100' : 'opacity-0'
      }`}
      style={{
        backgroundImage: bgImg,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

export default ImageHandler
